import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { membersKeys } from "@/features/members/queries/membersQueryKeys";
import membersService from "@/features/members/services/membersService";
import type { CreateMemberInput, CreateMemberResult } from "@/features/members/types";

/**
 * Mutation hook for creating a new member.
 * Invalidates members list on success and navigates to the new member's profile.
 */
export function useCreateMember() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async (input: CreateMemberInput): Promise<CreateMemberResult> => {
            return await membersService.createMember(input);
        },
        onError: (error) => {
            const message = error instanceof Error ? error.message : "Failed to add member. Please try again.";
            if (message.includes("already exists")) {
                toast.error("A member with this phone number already exists");
            } else {
                toast.error(message);
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: membersKeys.all,
            });
            toast.success("Member added successfully");
            navigate(`/members/${data.uid}`);
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
